<template>
    <div id="webstore-container">
        <CheckoutHeader
            :checkoutStep="{display: true, step: 1}"
            :categoryFilter="{display: activePage === 'products',
                all: webstoreContent.categoriesInfo && webstoreContent.categoriesInfo['All']['category'],
                devices: webstoreContent.categoriesInfo && webstoreContent.categoriesInfo['Devices']['category'],
                accessories: webstoreContent.categoriesInfo && webstoreContent.categoriesInfo['Accessories']['category']
            }"
            :enableTalkToSales="this.shouldButtonShowBasedOnDate"
            :showTalkToSales="this.showTalkToSalesButton"
            :shoppingBag="{display: true}"
            :stickyOverwrite="activePage !== 'products'"
            :productCatalogPage="activePage === 'products'"
            @anonymous-inquiry="anonymousInquiryModal = true"
        />
        <LoadingScreen v-if="displayLoader" />
        <div v-else>
            <ProductCatalog
                v-if="activePage === 'products'"
                :webstoreProductContent="webstoreProductContent"
                @inquire-modal="openInquiryModal"
                @anonymous-modal="anonymousInquiryModal = true"
            />
            <AddProduct
                v-if="activePage === 'addProduct'"
                :key="activeProductKey"
                :webstoreProductContent="webstoreProductContent"
            />
            <AddAccessory
                v-if="activePage === 'addAccessory'"
                :key="activeProductKey"
                @anonymous-inquiry="anonymousInquiryModal = true"
            />
            <InquireModal
                :isOpen="inquireModalIsOpen"
                :productName="inquiryName"
                :productKey="inquiryKey"
                homePage="true"
                @hide-modal="inquireModalIsOpen = false"/>
            <AnonymousInquiryModal v-if="anonymousInquiryModal" @hide-modal='anonymousInquiryModal = false'/>
            <ShoppingCart />
            <WebstoreFooter />
        </div>
    </div>
</template>

<script>
    import axios from 'axios';
    import { computed } from "vue";
    import {mapGetters, mapMutations, mapActions} from 'vuex';
    import ShoppingCart from '../shopping-cart';
    import CheckoutHeader from '../../layouts/header';
    import AddProduct from '../add-product';
    import ProductCatalog from '../product-catalog';
    import AddAccessory from '../add-accessory';
    import LoadingScreen from '../loading-screen';
    import { productToFamily, coreProducts } from '../../../../settings';
    import { analyticsIdentify, analyticsUserTraitsIdentified }  from '../../../../plugins/segment';
    import WebstoreFooter from "../components/WebstoreFooter.vue";
    import { WebstoreResources } from "../../../../mixins/webstore-resources"
    import { getFormValueLocal }  from '../../../../plugins/getLocalStorage';
    import AnonymousInquiryModal from "../AnonymousInquiryModal.vue"
    import InquireModal from '../../../checkouts/webstore/inquire-modal.vue';

    export default {

        mixins: {
            WebstoreResources
        },

        components: {
            CheckoutHeader,
            ShoppingCart,
            AddProduct,
            AddAccessory,
            LoadingScreen,
            WebstoreFooter,
            ProductCatalog,
            InquireModal,
            AnonymousInquiryModal
        },

        data() {
            return {
                    displayLoader: true,
                    productToFamily: productToFamily,
                    coreProducts: coreProducts,
                    shouldButtonShowBasedOnDate: true, 
                    showTalkToSalesButton: false,
                    webstoreProductContent: {},
                    ownerEmail: '',
                    inquireModalIsOpen: false,
                    anonymousInquiryModal: false,
                    inquiryName: '',
                    inquiryKey: ''
                }
        },
        provide() {
            return{
                ownerEmail: computed(() => this.ownerEmail)
            }
        },
        computed: {
            ...mapGetters([
                'productCatalog',
                'activeProductKey',
                'shoppingCartIsOpen',
                'selectedCategory',
                'isAccountCreatedAfterTitan',
                'activeProductTab',
                'isAnonymousCustomer'
            ]),

            ...mapGetters('content', [
                'webstoreContent',
                'footerContent',
            ]),

            ...mapGetters('checkout', [
                'checkoutType',
                'smallFleetOpportunity',
                'accountId',
                'userEmail',
                'source',
                'isDelinquent',
                'hideLicenses',
                'hasContractExpired',
                'contract',
                'isENT',
                'currencyIsoCode'
            ]),

            ...mapGetters('shoppingCart', [
                'productsInCart',
                'couldProductInCartHaveLicenses'
            ]),

            activePage() {
                if (this.activeProductKey === '') {
                    return 'products';
                } else if (coreProducts.includes(this.activeProductKey)) { 
                    return 'addProduct';
                } else {
                    return 'addAccessory';
                }
            },
        },

        methods: {
            ...mapMutations([
                'setProductCatalog',
                'setCurrencySymbol',
                'setisAccountCreatedAfterTitan',
                'setActiveProductKey',
                'setActiveProductFamily',
                'setActiveProductTab',
                'setSFDCSegments',
                'setPricingModal',
                'setIsAnonymousCustomer',
                'setIsReferralIdCustomer',
                'setPartnerAccount',
                'setVGBasicCustomer'
            ]),

            ...mapMutations('checkout', [
                'setAccountId',
                'setUserEmail',
                'setSource',
                'setIsENT',
                'setCheckoutType',
                'setCurrencyIsoCode',
                'setWrittenLanguage',
                'setBillingCountry',
                'setIsDelinquent',
                'setIsSelfServiced',
                'setHideLicenses',
                'setHasContractExpired',
                'setContractEndDate',
                'setIsContractOnFile',
                'setSmallFleetOpportunity',
                'setSafetyEventReview',
                'setSafetyEventReviewPrice',
                'setFirstPurchase'
            ]),

            ...mapMutations('billing', [
                'setCountry'
            ]),

            ...mapActions('content', [
                'getWebstoreContent',
                'getWebstoreProductContent',
            ]),
            openInquiryModal(inquiryObj){
                this.inquireModalIsOpen = true;
                this.inquiryName = inquiryObj.name;
                this.inquiryKey = inquiryObj.key;
            },
            changeSelectedCategory: function(selectedCategory) {
                this.selectedCategory = selectedCategory;
            },

            initializeWebstoreStoreData: async function(webstoreData) {
                this.setProductCatalog(webstoreData.data.product_catalog);
                this.setCurrencySymbol(webstoreData.data.currency_symbol);
                this.setIsENT(webstoreData.data.is_ENT)
                this.setCurrencyIsoCode(webstoreData.data.currency_code);
                this.setAccountId(webstoreData.data.account_id);
                this.setUserEmail(webstoreData.data.user_email);
                this.setSource(webstoreData.data.source);
                this.setCheckoutType(webstoreData.data.checkout_type);
                this.setWrittenLanguage(webstoreData.data.written_language);
                this.setCountry(webstoreData.data.billing_country);
                this.setIsDelinquent(webstoreData.data.is_delinquent);
                this.setIsSelfServiced(webstoreData.data.is_self_serviced);
                this.setHideLicenses(webstoreData.data.hide_licenses);
                this.setHasContractExpired(webstoreData.data.has_contract_expired)
                this.setContractEndDate(webstoreData.data.contract_end_date);
                this.setIsContractOnFile(webstoreData.data.is_contract_on_file);
                this.setSmallFleetOpportunity(webstoreData.data.is_small_fleet);
                this.setFirstPurchase(webstoreData.data.first_purchase);
                this.setSafetyEventReview(webstoreData.data.safety_event_review_activated);
                this.setSafetyEventReviewPrice(webstoreData.data.safety_event_review_price);
                this.setisAccountCreatedAfterTitan(webstoreData.data.is_account_created_after_titan);
                this.setIsAnonymousCustomer(webstoreData.data.is_anonymous_customer);
                this.setIsReferralIdCustomer(webstoreData.data.is_referral_id);
                this.setPartnerAccount(webstoreData.data.is_partner_account);
                this.setVGBasicCustomer(webstoreData.data.is_vg_basic_customer);
                if(this.isAnonymousCustomer){
                    this.setPricingModal(true);
                }
                this.setSFDCSegments({
                    segmentC: webstoreData.data.sfdc_segment_c,
                    ownerRole: webstoreData.data.sfdc_owner_role,
                })
                this.showTalkToSalesButton = webstoreData.data.talk_to_sales_segment;
                this.ownerEmail = webstoreData.data.owner_email;
                this.webstoreProductContent = await this.getWebstoreProductContent();
                await this.getWebstoreContent();
                
            },

            handleProductRouting: function() {
                if (this.$router.currentRoute.query.product) {
                    if (this.$router.currentRoute.query.product == 'none') {
                        this.backToWebstoreHome()
                    } else {
                        this.setActiveProductKey(this.$router.currentRoute.query.product)
                        if (this.productToFamily && this.productToFamily[this.$router.currentRoute.query.product]) {
                            this.setActiveProductFamily(this.productToFamily[this.$router.currentRoute.query.product])
                        } else if (this.$router.currentRoute.query.product.includes("CBL")) {
                            this.setActiveProductFamily('CBL')
                        } else {
                            this.setActiveProductFamily('ACC')
                        }
                    }
                }
                if (this.$router.currentRoute.query.tab) {
                    this.setActiveProductTab(this.$router.currentRoute.query.tab)
                }
            },

            handleRefresh: function() {
                if (this.$router.currentRoute.query.refresh) {
                    // Replace products in cart with the ~potentially~ updated prices from updated productCatalog
                    this.productsInCart.forEach(inCartOffer => {
                        let offerLookup = this.productCatalog[inCartOffer?.productFamily][inCartOffer?.productKey]?.offerings[inCartOffer?.offerKey]
                        inCartOffer.listPrice = offerLookup?.listPrice
                        inCartOffer.salesPrice = offerLookup?.salesPrice
                    });
                    // Route to review-cart 
                    this.$router.push({path: '/review-cart'});
                }
            },
        },

        async created() {
            try {
                let webstoreData = await axios('/webstore/get-select-products-page-data');
                await this.initializeWebstoreStoreData(webstoreData);

                // Handle intercom setup for only small fleet first purchases
                try {
                    if (this.smallFleetOpportunity && webstoreData.data.first_purchase) {
                        this.$intercom.boot()
                        let intercomData = ["email", "phone", "company"].reduce((obj, val) => {if (getFormValueLocal(val)){obj[val] = getFormValueLocal(val)} return obj;}, {});
                        this.$intercom.update(intercomData);
                        }
                    }
                catch( e ) {
                    console.error(e)
                    console.error("Error updating Intercom");
                }
            } catch(e) {
                let errorType = e ? e.response ? e.response.data : e.response : e;

                if (errorType === 'unsupported_small_fleet_country') {
                    await this.getWebstoreContent();
                    this.$router.push({path: '/message', query: {type: 'unsupportedSmallFleetCountry'}});
                } else if (errorType === 'sessionExpired') {
                    this.$router.push({path: '/message', query: {type: 'sessionExpired'}});
                } else {
                    this.$router.push({path: '/message', query: {type: 'errorCheckingOut'}});
                }
                return;
            }

            // Prevent customers from purchasing anything when they have an expired contract.
            if (this.hasContractExpired) {
                this.$router.push({path: '/message', query: {type: 'contract_expired'}});
                return;
            }

            if (this.isDelinquent) {
                this.$router.push({path: '/message', query: {type: 'delinquent'}});
                return;
            }

            let isUserAccountIdentified = analyticsUserTraitsIdentified("accountId");

            // Handle auto-routing from bot links
            this.handleProductRouting();

            // Handle refresh to update prices for in cart items
            this.handleRefresh();

            if (!isUserAccountIdentified) {
                analyticsIdentify({
                    checkoutType: this.checkoutType,
                    isSelfServiced: this.isSelfServiced,
                    accountId: this.accountId,
                    isSmallFleetCustomer: this.smallFleetOpportunity,
                    source: this.source
                })
            }

            this.displayLoader = false;
        },
    }
</script>

<style scoped>

    #webstore-container {
        width: 100%;
        height: 100%;
        font-family: "Nitti Grotesk", sans-serif;
        font-style: normal;
        font-weight: normal;
        margin: 0 auto;
        padding-bottom: 0px;
    }

    h1, h2, h3, h4, h5, p {
        color: #00263E;
        line-height: 1em;
    }

</style>
